import {
  ProductQuery,
  ProductVariantOrderByWithRelationInput,
  SortOrder,
} from '@/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import { ActionIcon, Alert, Button, Group, Loader, NumberInput, Stack } from '@mantine/core';
import { useClipboard, useLocalStorage } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconCheck,
  IconClipboardCopy,
  IconEdit,
  IconNavigation,
  IconPlus,
  IconRefresh,
  IconShoppingCart,
  IconTrash,
} from '@tabler/icons-react';
import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { productDocument } from '../graphql/get-product';
import { productVariantDocument } from '../graphql/get-product-variant';
import {
  decrementProductVariantStockDocument,
  incrementProductVariantStockDocument,
  removeProductVariantFromOrdersDocument,
  removeProductVariantOrderLineDocument,
} from '../graphql/mutations';
import { AddVariantModal } from './AddVariantModal';

export const UpdateVariantStockModal = ({
  productVariantId,
  stock,
}: {
  productVariantId: string;
  stock: number;
}) => {
  const [value, setValue] = useState<number | string>(0);

  const [increment, incrementMutation] = useMutation(incrementProductVariantStockDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    notifyOnNetworkStatusChange: true,
  });

  const [decrement, decrementMutation] = useMutation(decrementProductVariantStockDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    notifyOnNetworkStatusChange: true,
  });

  const handleSubmit = async () => {
    if (typeof value !== 'number') return;

    if (value > 0) {
      await increment({ variables: { productVariantId, increment: value } });
    } else if (value < 0) {
      await decrement({ variables: { productVariantId, decrement: Math.abs(value) } });
    }

    setValue(0);

    modals.closeAll();
  };

  return (
    <Stack>
      <Alert variant="light" color="orange">
        Ak chcete odobrať kusy, zadajte záporné číslo. Ak chcete pridať kusy, zadajte kladné číslo.
        Aktuálny stav: {stock} ks. Možný stav po úprave:{' '}
        {stock + (typeof value === 'number' ? value : 0)} ks.
      </Alert>
      <NumberInput
        value={value}
        onChange={setValue}
        label="Koľko pridať/odobrať kusov?"
        allowDecimal={false}
      />
      <Button
        variant="filled"
        onClick={handleSubmit}
        loading={incrementMutation.loading || decrementMutation.loading}
      >
        Potvrdiť
      </Button>
    </Stack>
  );
};

const ProductVariantOrdersModal = ({
  productVariantId,
  name,
}: {
  productVariantId: string;
  name: string;
}) => {
  const { data, loading } = useQuery(productVariantDocument, {
    variables: { where: { id: productVariantId } },
  });

  const [removeProductVariantFromOrders, { loading: loadingRemove }] = useMutation(
    removeProductVariantFromOrdersDocument,
    {
      refetchQueries: 'active',
      awaitRefetchQueries: true,
      notifyOnNetworkStatusChange: true,
    }
  );

  const [removeProductVariantOrderLine, { loading: loadingRemoveLine }] = useMutation(
    removeProductVariantOrderLineDocument,
    {
      refetchQueries: 'active',
      awaitRefetchQueries: true,
      notifyOnNetworkStatusChange: true,
    }
  );

  const removeMessage = `Produkt [${name}] bol z vášho košíka odstránený, ospravedlňujeme sa za komplikácie.`;

  return (
    <DataTable
      withTableBorder
      striped
      highlightOnHover
      records={data?.productVariant.lines ?? []}
      fetching={loading}
      columns={[
        {
          accessor: 'order.package',
          title: 'Balík',
        },
        {
          accessor: 'price',
          title: 'Cena',
          render: ({ price }) => `${price} €`,
        },
        {
          accessor: 'order.total',
          title: 'Total objednávky',
          render: ({ order }) => `${order.total} €`,
        },
        {
          accessor: 'createdAt',
          title: 'Vytvorené',
          render: ({ createdAt }) => dayjs(createdAt).format('DD.MM. HH:mm'),
        },
        {
          accessor: 'order.customer.name',
          title: 'Zákazník',
        },
        {
          accessor: 'actions',
          title: (
            <ActionIcon
              loading={loadingRemove}
              size="sm"
              variant="subtle"
              color="red"
              onClick={() =>
                modals.openConfirmModal({
                  id: 'remove-product-variant-from-orders',
                  title: `Odstrániť variantu z ${data?.productVariant.lines?.length} objednávok`,
                  children: (
                    <Alert color="red" title="Odstrániť variantu z objednávok?" variant="filled">
                      Odstránením variantu z objednávok sa odstránia všetky objednávky, ktoré
                      obsahujú tento variant. Zákazníci dostanú správu o odstránení produktu.
                    </Alert>
                  ),
                  labels: { confirm: 'Odstrániť', cancel: 'Zrušiť' },
                  onConfirm: async () => {
                    await removeProductVariantFromOrders({
                      variables: {
                        productVariantId,
                        message: removeMessage,
                      },
                    });

                    modals.close('remove-product-variant-from-orders');
                  },
                })
              }
            >
              <IconTrash size={16} />
            </ActionIcon>
          ),
          render: ({ id: lineId, order: { id: orderId } }) => (
            <Group gap="xs" wrap="nowrap" justify="flex-end">
              <ActionIcon
                size="sm"
                variant="subtle"
                color="red"
                loading={loadingRemoveLine}
                onClick={() =>
                  modals.openConfirmModal({
                    id: `remove-order-line-${lineId}`,
                    title: 'Odstrániť položku z objednávky',
                    children: (
                      <Alert color="red" title="Odstrániť položku?" variant="filled">
                        Položka bude odstránená z objednávky, kus sa vráti na sklad, total
                        objednávky sa prepočíta a zákazník dostane správu o odstránení.
                      </Alert>
                    ),
                    labels: { confirm: 'Odstrániť', cancel: 'Zrušiť' },
                    onConfirm: async () => {
                      await removeProductVariantOrderLine({
                        variables: {
                          lineId,
                          message: removeMessage,
                        },
                      });

                      modals.close(`remove-order-line-${lineId}`);
                    },
                  })
                }
              >
                <IconTrash size={16} />
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="subtle"
                color="teal"
                onClick={() => window.open(`/order/${orderId}`, '_blank')?.focus()}
              >
                <IconNavigation size={16} />
              </ActionIcon>
            </Group>
          ),
        },
      ]}
    />
  );
};

export const ProductVariantsTable = ({ productId }: { productId: string }) => {
  const clipboard = useClipboard({ timeout: 500 });
  const [sortStatus, setSortStatus] = useLocalStorage<
    DataTableSortStatus<NonNullable<NonNullable<ProductQuery['product']>['variants']>[number]>
  >({
    key: 'productVariants-sortStatus',
    defaultValue: {
      columnAccessor: 'stock',
      direction: 'desc',
    },
  });

  const orderBy = useMemo<ProductVariantOrderByWithRelationInput>(() => {
    switch (sortStatus.columnAccessor) {
      case 'soldPrice':
        return { lines: { _count: sortStatus.direction as SortOrder } };
      case 'soldQuantity':
        return { lines: { _count: sortStatus.direction as SortOrder } };
      default:
        return { [sortStatus.columnAccessor]: sortStatus.direction };
    }
  }, [sortStatus]);

  const { data, loading, refetch } = useQuery(productDocument, {
    variables: {
      where: { id: productId },
      variantsOrdersBy: orderBy,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  if (loading) return <Loader />;

  if (!data?.product?.variants) return null;

  const productName = data.product.name;
  const existingAttributeNames = Array.from(
    new Set(data.product.variants.flatMap((v) => v.attributes?.map((a) => a.name) ?? []))
  );

  const openAddVariantModal = () =>
    modals.open({
      title: `Pridať variantu k ${productName}`,
      size: 'auto',
      children: (
        <AddVariantModal productId={productId} existingAttributeNames={existingAttributeNames} />
      ),
    });

  return (
    <DataTable
      withTableBorder
      striped
      highlightOnHover
      records={data.product.variants}
      sortStatus={sortStatus}
      onSortStatusChange={setSortStatus}
      columns={[
        {
          accessor: 'attributes',
          title: 'Varianty',
          hidden: data.product.variants.length === 1,
          render: ({ attributes }) => attributes?.map(({ value }) => `${value}`).join(' '),
          cellsStyle: ({ stock }) =>
            stock < 5 ? { color: 'orange.5' } : stock === 0 ? { color: 'red.5' } : undefined,
        },
        {
          accessor: 'stock',
          title: 'Skladom',
          render: ({ stock }) => `${stock} ks`,
          footer: (
            <Group>
              {data.product.variants.reduce((quantity, variant) => quantity + variant.stock, 0)} ks
            </Group>
          ),
        },
        {
          accessor: 'soldPrice',
          title: 'Predané (€)',
          render: ({ lines }) => `${lines?.reduce((sum, line) => sum + line.price, 0) ?? 0} €`,
          footer: (
            <Group>
              {data.product.variants.reduce(
                (price, variant) =>
                  price + (variant.lines?.reduce((sum, line) => sum + line.price, 0) ?? 0),
                0
              )}{' '}
              €
            </Group>
          ),
        },
        {
          accessor: 'soldQuantity',
          title: 'Predané (ks)',
          render: ({ lines }) => `${lines?.reduce((sum) => sum + 1, 0) ?? 0} pcs`,
          footer: (
            <Group>
              {data.product.variants.reduce(
                (quantity, variant) => quantity + (variant.lines?.reduce((sum) => sum + 1, 0) ?? 0),
                0
              )}{' '}
              ks
            </Group>
          ),
        },
        {
          accessor: 'reservations',
          title: 'Rezervácie',
          render: ({ reservations }) =>
            data.product?.variants?.length === 1
              ? `${data.product.reservations.length} ks`
              : `${reservations?.length ?? 0} ks`,
          footer: (
            <Group>
              {data.product.variants?.reduce(
                (total, variant) => total + (variant.reservations.length ?? 0),
                0
              ) + data.product.reservations.length}{' '}
              ks
            </Group>
          ),
        },
        {
          accessor: 'actions',
          textAlign: 'right',
          title: (
            <Group justify="flex-end" gap="xs">
              <ActionIcon
                size="sm"
                variant="subtle"
                color="green"
                onClick={() => {
                  void refetch();
                }}
                loading={loading}
              >
                <IconRefresh size={16} />
              </ActionIcon>
              <ActionIcon size="sm" variant="subtle" onClick={openAddVariantModal}>
                <IconPlus size={16} />
              </ActionIcon>
            </Group>
          ),
          render: ({ id, attributes, stock }) => (
            <Group>
              <ActionIcon
                size="sm"
                variant="subtle"
                color="blue"
                onClick={() =>
                  modals.open({
                    title: `Upraviť sklad pre ${data.product?.code} ${attributes?.map(({ value }) => `${value}`).join(' ')} `,
                    children: <UpdateVariantStockModal productVariantId={id} stock={stock} />,
                  })
                }
              >
                <IconEdit size={16} />
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="subtle"
                color="teal"
                onClick={() =>
                  modals.open({
                    title: `Objednávky pre ${attributes?.map(({ value }) => `${value}`).join(' ')}`,
                    size: '80%',
                    children: (
                      <ProductVariantOrdersModal
                        productVariantId={id}
                        name={`${data.product?.code} ${attributes?.map(({ value }) => `${value}`).join(' ')} ${data.product?.price} €`}
                      />
                    ),
                  })
                }
              >
                <IconShoppingCart size={16} />
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="subtle"
                color={clipboard.copied ? 'teal' : 'gray'}
                onClick={() => {
                  clipboard.copy(id);
                }}
              >
                {clipboard.copied ? <IconCheck size={16} /> : <IconClipboardCopy size={16} />}
              </ActionIcon>
            </Group>
          ),
        },
      ]}
    />
  );
};
